import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { format, addDays, subDays, startOfDay, eachDayOfInterval } from 'date-fns';
import { fr } from 'date-fns/locale';

export const useTimeline = ({ vue, loading }) => {
  const scrollContainerRef = useRef(null);
  const [currentMonth, setCurrentMonth] = useState('');

  const aujourdhuiDate = useMemo(() => startOfDay(new Date()), []);
  const aujourdhuiStr = useMemo(
    () => format(aujourdhuiDate, 'yyyy-MM-dd'),
    [aujourdhuiDate]
  );

  const joursTimeline = useMemo(() => {
    const jours = eachDayOfInterval({
      start: subDays(aujourdhuiDate, 30),
      end: addDays(aujourdhuiDate, 365),
    });
    return jours.map((j) => ({
      dateObj: j,
      isoStr: format(j, 'yyyy-MM-dd'),
      moisStr: format(j, 'MMMM yyyy', { locale: fr }),
    }));
  }, [aujourdhuiDate]);

  const indexToday = joursTimeline.findIndex((j) => j.isoStr === aujourdhuiStr);

  const largeurCellule = useMemo(() => {
    if (vue === 1) return 240;
    if (vue === 7) return 160;
    if (vue === 15) return 120;
    return 90;
  }, [vue]);

  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current) return;

    const scrollLeft = scrollContainerRef.current.scrollLeft;
    const visibleDayIndex = Math.round(scrollLeft / largeurCellule);

    if (joursTimeline[visibleDayIndex]) {
      const mois = joursTimeline[visibleDayIndex].moisStr;
      setCurrentMonth(mois.charAt(0).toUpperCase() + mois.slice(1));
    }
  }, [joursTimeline, largeurCellule]);

  const scrollByDays = (direction, daysCount = vue) => {
    const distance = daysCount * largeurCellule;
    scrollContainerRef.current?.scrollBy({
      left: direction === 'left' ? -distance : distance,
      behavior: 'smooth',
    });
  };

  const scrollToToday = () => {
    scrollContainerRef.current?.scrollTo({
      left: indexToday * largeurCellule,
      behavior: 'smooth',
    });
  };

  // Positionnement initial sur "aujourd'hui"
  useEffect(() => {
    if (!loading && scrollContainerRef.current && indexToday !== -1) {
      scrollContainerRef.current.scrollLeft = indexToday * largeurCellule;
      handleScroll();
    }
  }, [loading, indexToday, largeurCellule, handleScroll]);

  return {
    scrollContainerRef,
    currentMonth,
    aujourdhuiStr,
    joursTimeline,
    indexToday,
    largeurCellule,
    handleScroll,
    scrollByDays,
    scrollToToday,
  };
};
